import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import deepEqual from 'deep-equal';
import { useDebouncedCallback } from 'use-debounce';

import {
  useGetAllNotificationsQuery,
  useGetUnreadNotificationsQuery,
  useUpdateNotificationAsReadMutation
} from '@api/notifications';
import {
  markUserMessageAsRead,
  setStoredUserNotifications,
  setStoreNotifications,
  setInitialLoad
} from '@components/Notification/slice';
import { RootState } from '@store/store';
import {
  MSG_EVENT,
  MSG_TYPE,
  NotificationData,
  NotificationRes
} from '@DStypes/notifications';
import { getNotificationMessageWithLink } from '@utils/notifications';
import UserAuth from '@services/auth';

import useToast from './useToast';
import useCustomNavigate from './useCustomNavigate';
import useDownloadReport from './useDownloadReport';
import { useLazyGetProjectByIdQuery } from '@api/projects';
import { pbWithChartsId } from '@constants/common';
import useUserDetails from './useUserDetails';

const skipNotificationArray = [
  MSG_EVENT.FIELD_CATEGORISATION_SUCCESS,
  MSG_EVENT.FIELD_CATEGORISATION_FAILED,
  MSG_EVENT.DASHBOARD_FILTER_SUCCESS,
  MSG_EVENT.DASHBOARD_FILTER_REMOVED,
  MSG_EVENT.DASHBOARD_FILTER_FAILED,
  MSG_EVENT.PLAN_UPDATED
];

const useNotifications = () => {
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const notificationsData = useSelector(
    (state: RootState) => state?.notificationsData
  );

  const dispatch = useDispatch();
  const { downloadReport, downloadCleanedFile } = useDownloadReport();

  const { pushSuccessToast, pushErrorToast, pushInfoToast, pushToast } =
    useToast();
  const [getProjectById] = useLazyGetProjectByIdQuery();
  const user = UserAuth.userInfo;
  const { getUserDetails } = useUserDetails();
  const projectData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData
  );

  let skipArray: string[] = [];

  const navigate = useCustomNavigate();

  const isInitialRender = useRef(true);

  const [markAsRead] = useUpdateNotificationAsReadMutation();

  const isPbWithCharts = Boolean(document.getElementById(pbWithChartsId));

  if (isPbWithCharts)
    skipArray = [
      ...skipNotificationArray,
      MSG_EVENT.FILE_COMBINE_SUCCESS,
      MSG_EVENT.FILE_COMBINE_UNSUCCESSFUL,
      MSG_EVENT.FILE_COMBINE_FAIL,
      MSG_EVENT.CLOUD_UPDATE_COMPLETED,
      MSG_EVENT.DATA_RECALCULATION_COMPLETED
    ];
  else skipArray = skipNotificationArray;

  const { data: notifications, isSuccess } = useGetUnreadNotificationsQuery(
    userDetails.user.id as string,
    {
      pollingInterval: 2000,
      skip: !UserAuth.isAuthenticated || !userDetails.user.id
    }
  );
  const { data: allNotifications, refetch } = useGetAllNotificationsQuery(
    userDetails.user.id as string,
    {
      skip: !UserAuth.isAuthenticated || !userDetails.user.id
    }
  );
  const toastNotifications = useDebouncedCallback(() => {
    const newUnreadUsrMsgs = notificationsData?.userMessages?.filter(
      (msg) =>
        !msg.is_read &&
        !notificationsData?.storedUserMessages?.some(
          (item: any) => item.id === msg.id
        )
    );

    const newNotifications = [
      ...(notifications?.unreadMessages?.filter(
        (e: any) =>
          !notificationsData?.unreadMessages?.some(
            (item: any) => item.id === e.id
          )
      ) || []),
      ...(newUnreadUsrMsgs || [])
    ];

    let unread: NotificationData[] =
      notifications?.unreadMessages as NotificationData[];
    let userUnreadMessages: NotificationData[] =
      notificationsData?.userMessages?.filter(
        (msg) => !msg.is_read
      ) as NotificationData[];

    const showNotifications = () =>
      newNotifications.forEach(async (notification: NotificationData) => {
        if (!skipArray.includes(notification.message_event)) {
          if (notification.message_event === MSG_EVENT.CLOUD_UPDATE_COMPLETED) {
            const userId = user?.id;

            if (UserAuth.isAuthenticated && userId)
              getUserDetails(userId as string);
          }

          const message =
            notification.message_event === MSG_EVENT.REPORT_GENERATION_COMPLETED
              ? getNotificationMessageWithLink(
                  notification,
                  navigate,
                  false,
                  (id: string, name: string) => {
                    downloadReport(id, name);
                  }
                )
              : notification.message_event ===
                MSG_EVENT.CLEANED_FILE_GENERATION_COMPLETED
              ? getNotificationMessageWithLink(
                  notification,
                  navigate,
                  false,
                  (id: string, name: string) => {
                    downloadCleanedFile(id, name);
                  }
                )
              : notification.message_event ===
                MSG_EVENT.CLEANED_FILE_GENERATION_FAILED
              ? !location.pathname.includes(
                  JSON.parse(notification.message_data)?.project?.id
                )
                ? getNotificationMessageWithLink(notification, navigate, false)
                : notification.message_text
              : getNotificationMessageWithLink(notification, navigate, false);

          if (message && !notification?.is_silent)
            switch (notification.message_type) {
              case MSG_TYPE.SUCCESS:
                pushSuccessToast(message);
                break;
              case MSG_TYPE.ERROR:
                pushErrorToast(message);
                break;
              case MSG_TYPE.INFO:
                pushInfoToast(message, {
                  position:
                    notification.message_event ===
                    MSG_EVENT.TIME_SERIES_DISABLED
                      ? 'top-center'
                      : 'top-right'
                });
                break;
              case MSG_TYPE.WARNING:
                pushToast(message, {
                  color: 'warning'
                });
                break;
            }

          if (!notification.is_user_message && !notification.is_silent) {
            unread = unread.filter((msg) => msg.id !== notification.id);
            await markAsRead({
              id: userDetails.user.id as string,
              message_id: [notification.id as string]
            });
          } else if (notification.is_user_message && !notification.is_silent) {
            dispatch(
              markUserMessageAsRead({
                notificationsData,
                message_id: notification.id
              })
            );
          }

          userUnreadMessages = notification?.is_silent
            ? (notificationsData?.userMessages?.filter(
                (message) => !message.is_read
              ) as NotificationData[])
            : (notificationsData?.userMessages?.filter(
                (message) => message.id !== notification.id && !message.is_read
              ) as NotificationData[]);

          unread = notification.is_silent
            ? (notifications?.unreadMessages as NotificationData[])
            : (notifications?.unreadMessages?.filter(
                (message) => message.id !== notification.id
              ) as NotificationData[]);

          if (notification.message_event === MSG_EVENT.ADD_FILE_FAIL)
            setTimeout(() => window.location.reload(), 1000);
          if (
            notification.message_event === MSG_EVENT.FILE_COMBINE_CANCELLED ||
            notification.message_event === MSG_EVENT.CLOUD_UPDATE_COMPLETED ||
            notification.message_event ===
              MSG_EVENT.DATA_RECALCULATION_COMPLETED
          )
            getProjectById({ projectId: projectData?.id });
        }
      });

    if (!notificationsData?.isInitialLoad)
      if (!isInitialRender.current && document.hasFocus()) showNotifications();
      else
        setTimeout(() => {
          if (!isInitialRender.current && document.hasFocus())
            showNotifications();
        }, 1000);

    const addedNotifications: NotificationRes = {
      ...notifications
    };

    const allMessages = [
      ...(notificationsData.userMessages || []),
      ...(allNotifications?.allMessages || [])
    ];

    const allUnreadMessages = [
      ...(unread || []),
      ...(userUnreadMessages || [])
    ];

    addedNotifications.allMessages = allMessages.sort((a: any, b: any) => {
      const first = a.created_at;
      const second = b.created_at;

      if (first > second) return -1;

      if (second < first) return 1;

      return 0;
    });

    addedNotifications.unreadMessages = allUnreadMessages.sort(
      (a: any, b: any) => {
        const first = a.created_at;
        const second = b.created_at;

        if (first > second) return -1;

        if (second < first) return 1;

        return 0;
      }
    );

    isInitialRender.current = false;
    dispatch(setStoreNotifications(addedNotifications));
    if (isSuccess) dispatch(setInitialLoad());
  }, 500);

  useEffect(() => {
    if (
      !deepEqual(
        notifications?.unreadMessages,
        notificationsData.unreadMessages
      ) ||
      notificationsData?.userMessages
    ) {
      if ((UserAuth.isAuthenticated || userDetails.user.id) && allNotifications)
        refetch();
      toastNotifications();
      setTimeout(() => {
        dispatch(setStoredUserNotifications(notificationsData.userMessages));
      }, 500);
    }
  }, [notifications, notificationsData.userMessages]);

  return {
    notificationsData
  };
};

export default useNotifications;
